import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import PackageCard from "./PackageCard";
import OfferManager from "../../../services/home/OfferManager";
import CouponManager from "../../../services/home/CouponManager";
import PriceBreakdownCard from "./PriceBreakdownCard";
import { AnimatePresence, motion } from "framer-motion";
import {
  subscribeUser,
  fetchCurrentSubscription,
  changeSubscriptionPlan,
  fetchUpcomingSubscription,
} from "../../../features/subscriptionSlice";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import LoginPromptModal from "../authComponents/LoginPromptModal";
import CityManager from "../../../services/dashboard/CityManager";
import { Sprout, Flower2, Trees, TreeDeciduous } from "lucide-react"; // You can customize or use illustrations instead

const isMobile = window.innerWidth <= 768;

const PackagePlanCard = ({ plan, subscriptionType = "NEW", isUserLocationServiceable }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedPkgId, setSelectedPkgId] = useState(null);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [offers, setOffers] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCouponId, setAppliedCouponId] = useState(null);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [activeCities, setActiveCities] = useState([]);

  const dispatch = useDispatch();
  const status = useSelector((s) => s.subscription.status);
  const subscription = useSelector((s) => s.subscription.subscription);
  const subscriptionHistory = useSelector((s) => s.subscription.history);
  const subscriptionRes = useSelector((s) => s.subscription.subscription);
  const activeSubscription =
    subscriptionRes?.status === "ACTIVE" ? subscriptionRes : null;
  const upcomingSubscription = useSelector(
    (s) => s.subscription.upcomingSubscription
  );
  const scheduledSubscription =
    upcomingSubscription?.status === "SCHEDULED" ? upcomingSubscription : null;

  // Check if user has ever had a subscription (including expired ones)
  const hasEverHadSubscription = subscriptionHistory && subscriptionHistory.length > 0;

  const navigate = useNavigate();
  const userId = useSelector((s) => s.auth.user?.userId);
  const user = useSelector((s) => s.auth.user);
  const currentPlanId = activeSubscription?.planId;
  const currentPackageId = activeSubscription?.packageId;

  const containerRef = useRef(null);
  const popupRef = useRef(null); // For the popup element
  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

  const userPincode = user?.pincode;

  const flipVariants = {
    hidden: {
      rotateY: 90,
      opacity: 0,
    },
    visible: {
      rotateY: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeInOut",
      },
    },
    exit: {
      rotateY: -90,
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: "easeInOut",
      },
    },
  };

  useEffect(() => {
    OfferManager.getActiveOffers().then((res) => {
      setOffers(res.responseData || []);
    });
  }, []);

  const getBestOfferForPackage = (pkg) => {
    const applicableOffers = offers.filter(
      (offer) => {
        // Check if offer applies to this subscription type
        const subscriptionTypeMatches = offer.applicableSubscriptionType === "ALL" || 
                                       offer.applicableSubscriptionType === subscriptionType;
        
        // Check if offer applies to this package/plan
        const targetingMatches = (offer.bookPackageId === null && offer.bookPackagePlanId === null) ||
                               offer.bookPackageId === pkg.id ||
                               offer.bookPackagePlanId === pkg.planId;
        
        return subscriptionTypeMatches && targetingMatches;
      }
    );

    const best = applicableOffers.reduce(
      (max, curr) =>
        curr.discountPercentage > max.discountPercentage ? curr : max,
      { discountPercentage: 0 }
    );

    return best.discountPercentage;
  };

  // in PackagePlanCard.jsx, up near your other functions:
  const applyCoupon = async (code) => {
    if (!code) {
      toast.error("Please enter a coupon code.");
      return 0;
    }

    const res = await CouponManager.validateCouponByCode({
      code,
      userId,
      planId: plan.planId,
      packageId: selectedPkgId,
    });

    // Check errorCode first
    if (res.errorCode || !res.responseData?.valid) {
      toast.error(res.responseMessage || "Invalid coupon");
      setCouponDiscount(0);
      setAppliedCouponId(null);
      return 0;
    }

    // Coupon is valid
    const dto = res.responseData;
    const pkg = plan.packages.find((p) => p.packageId === selectedPkgId);
    const offerDisc = (pkg.price * getBestOfferForPackage(pkg)) / 100;
    const postOffer = pkg.price - offerDisc;
    const rupeeDiscount = Math.round((postOffer * dto.discountAmount) / 100);

    setCouponDiscount(rupeeDiscount);
    setAppliedCouponId(dto.couponId);
    toast.success(`Coupon applied! You saved ₹${rupeeDiscount}`);
    return rupeeDiscount;
  };

  useEffect(() => {
    function handleClickOutside(e) {
      // Only unselect if clicking outside of the package plan card and popup
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        (!popupRef.current || !popupRef.current.contains(e.target))
      ) {
        setSelectedPkgId(null); // Unselect the package if clicking outside the entire card
        setCouponCode("");
        setCouponDiscount(0);
        setAppliedCouponId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (couponCode === "") {
      setCouponDiscount(0);
      setAppliedCouponId(null);
    }
  }, [couponCode]);

  const booksFromType = (type) => {
    switch (type) {
      case "ONE_BOOK":
        return 1;
      case "TWO_BOOKS":
        return 2;
      case "THREE_BOOKS":
        return 3;
      default:
        return 0;
    }
  };

  useEffect(() => {
    if (expanded && plan.planId === currentPlanId && currentPackageId) {
      setSelectedPkgId(currentPackageId);
    } else if (!expanded) {
      setSelectedPkgId(null);
    }
  }, [expanded]);

  const toggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchUpcomingSubscription(userId));
    }
  }, [dispatch, userId]);

  const initiatePayment = (subscriptionType, couponId = null) => {
    if (!userId) {
      toast.error("User not logged in!");
      setShowLoginPrompt(true);
      return;
    }

    if (!isUserLocationServiceable) {
      toast.error("Sorry, subscription is not available in your area.");
      return;
    }

    if (!selectedPkgId) {
      toast.error("Please select a package.");
      return;
    }

    fetch(`${config.url}/api/payments/create-order`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId,
        planId: plan.planId,
        packageId: selectedPkgId,
        couponId,
      }),
    })
      .then((r) => r.json())
      .then((res) => {
        // Check for errors in response
        if (res.responseCode !== 200 || !res.responseData) {
          toast.error(res.responseMessage || "Failed to create payment order");
          return;
        }

        const { razorpayOrderId, amount } = res.responseData;

        const options = {
          key: "rzp_live_gHTTFxw5pxLmtX",
          amount: amount * 100,
          currency: "INR",
          name: "Books At Doorstep",
          description: ` Subscription Payment`,
          order_id: razorpayOrderId,
          handler: function (response) {
            fetch(`${config.url}/api/payments/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              }),
            })
              .then((r) => r.json())
              .then((data) => {
                if (data.responseCode === 200) {
                  toast.success("Payment successful!");
                  dispatch(fetchCurrentSubscription(userId));
                  navigate("/subscriptionsuccess");
                } else {
                  toast.error("Payment failed. You will be refunded shortly.");
                }
              });
          },
          prefill: {
            name: fullName,
            email: user?.emailId || "email@example.com",
            contact: user?.mobileNumber || "0000000000",
          },
          theme: {
            color: "#FF7236",
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      })
      .catch((err) => toast.error(`Payment initiation failed: ${err.message}`));
  };

  const handleSubscribe = () => initiatePayment("NEW", appliedCouponId);

  const handleUpgrade = () => {
    if (!plan.planId || !selectedPkgId) {
      toast.error("Please select a new plan and package to upgrade to.");
      return;
    }

    // 1) If a scheduled plan exists, show toast and do nothing:
    if (subscription?.status === "SCHEDULED") {
      toast.error(
        "You already have a plan scheduled. Please wait until it takes effect."
      );
      return;
    }

    // 2) Otherwise, open the price breakdown panel:
    setShowPriceBreakdown(true);
  };

  const getPlanIcon = (planName) => {
    const iconStyle = { color: "#4CAF50" }; // Uniform green color

    switch (planName) {
      case "Sprout Plan":
        return <Sprout style={iconStyle} size={20} />;
      case "Sapling Plan":
        return <Flower2 style={iconStyle} size={20} />;
      case "Branch Plan":
        return <Trees style={iconStyle} size={20} />;
      case "Tree Plan":
        return <TreeDeciduous style={iconStyle} size={20} />;
      default:
        return null;
    }
  };


  return (
    <>
      <div
        ref={containerRef}
        className="border border-primary rounded-xl mb-4 p-4 bg-white"
      >
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={toggleExpand}
        >
          <div className="flex flex-wrap items-center gap-2">
            {getPlanIcon(plan.planName)}
            <h2 className="text-base font-semibold text-primary">
              {plan.planName || plan.membershipPlan.replace("_", " ")} (
              {plan.validityDays} Days)
            </h2>
            {plan.planTagline && (
              <span className="text-sm text-gray-500">
                | {plan.planTagline}
              </span>
            )}
          </div>

          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>

        {expanded && (
          <>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
              {plan.packages
                .slice()
                .sort((a, b) => (a.price || 0) - (b.price || 0))
                .map((pkg) => {
                  const isCurrent =
                    activeSubscription?.planId === plan.planId &&
                    activeSubscription?.packageId === pkg.packageId;

                  const isUpcoming =
                    scheduledSubscription?.planId === plan.planId &&
                    scheduledSubscription?.packageId === pkg.packageId;

                  const books = booksFromType(pkg.packageType);
                  const firstMonthTotal =
                    (pkg.price || 0) +
                    (hasEverHadSubscription ? 0 : (pkg.oneTimeRegistration || 0)) +
                    (hasEverHadSubscription ? 0 : (pkg.refundDeposit || 0));

                  const isSelected = pkg.packageId === selectedPkgId;

                  return (
                    <div
                      key={pkg.packageId}
                      className="col-span-1 md:col-span-1"
                    >
                      <AnimatePresence mode="wait">
                        {isCurrent || isUpcoming ? (
                          <motion.div
                            key={isCurrent ? "current-card" : "upcoming-card"}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4 }}
                          >
                            <PackageCard
                              books={books}
                              price={pkg.price}
                              planValue={pkg.price}
                              registration={hasEverHadSubscription ? 0 : pkg.oneTimeRegistration}
                              deposit={hasEverHadSubscription ? 0 : pkg.refundDeposit}
                              firstMonthTotal={firstMonthTotal}
                              discountPercentage={getBestOfferForPackage(pkg)}
                              renewal={pkg.price}
                              selected={false}
                              disabled={true}
                              packDescription={plan.packDescription}
                              packageName={pkg.packageName}
                              scheduledSubscription={
                                isCurrent || isUpcoming
                                  ? null
                                  : scheduledSubscription
                              }
                              highlightLabel={
                                isCurrent
                                  ? "Current Package"
                                  : "Upcoming Package"
                              }
                              onSelect={() => {}} // no action for disabled
                            />
                          </motion.div>
                        ) : isSelected && showPriceBreakdown ? (
                          <motion.div
                            key="price-breakdown"
                            variants={flipVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="w-full h-full"
                          >
                            <PriceBreakdownCard
                              originalPrice={pkg.price}
                              offerDiscount={
                                (pkg.price * getBestOfferForPackage(pkg)) / 100
                              }
                              registration={
                                hasEverHadSubscription ? 0 : pkg.oneTimeRegistration
                              }
                              deposit={hasEverHadSubscription ? 0 : pkg.refundDeposit}
                              onCancel={() => {
                                setShowPriceBreakdown(false);
                                setSelectedPkgId(null);
                                setCouponCode("");
                                setCouponDiscount(0);
                                setAppliedCouponId(null);
                              }}
                              onApplyCoupon={applyCoupon}
                              onRemoveCoupon={() => setAppliedCouponId(null)}
                              onProceed={() =>
                                initiatePayment(
                                  plan.planId === currentPlanId
                                    ? "CHANGE_PLAN"
                                    : "NEW",
                                  appliedCouponId
                                )
                              }
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="package-card"
                            initial={{ rotateY: -90, opacity: 0 }}
                            animate={{ rotateY: 0, opacity: 1 }}
                            exit={{ rotateY: 90, opacity: 0 }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                            className="w-full h-full"
                          >
                            <PackageCard
                              books={books}
                              price={pkg.price}
                              planValue={pkg.price}
                              registration={hasEverHadSubscription ? 0 : pkg.oneTimeRegistration}
                              deposit={hasEverHadSubscription ? 0 : pkg.refundDeposit}
                              firstMonthTotal={firstMonthTotal}
                              discountPercentage={getBestOfferForPackage(pkg)}
                              renewal={pkg.price}
                              selected={isSelected}
                              disabled={false}
                              packageId={pkg.packageId}
                              activePackageId={subscription?.package?.packageId}
                              packDescription={plan.packDescription}
                              packageName={pkg.packageName}
                              highlightLabel={pkg.highlightLabel}
                              onSelect={() => {
                                if (isSelected) {
                                  setSelectedPkgId(null);
                                  setShowPriceBreakdown(false);
                                  setCouponCode("");
                                  setCouponDiscount(0);
                                  setAppliedCouponId(null);
                                } else {
                                  setSelectedPkgId(pkg.packageId);
                                  if (isMobile) {
                                    setShowPriceBreakdown(true);
                                  } else {
                                    setShowPriceBreakdown(false);
                                  }
                                }
                              }}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
            </div>

            <div className="mt-6 text-center">
              {!isMobile &&
                !scheduledSubscription && // 🚫 block button if upcoming exists
                (!subscription ? (
                  <button
                    onClick={() => setShowPriceBreakdown(true)}
                    disabled={!selectedPkgId || status === "loading"}
                    className="bg-primary text-white py-3 px-8 rounded-full hover:shadow-lg disabled:opacity-50 transition"
                  >
                    {status === "loading" ? "Processing..." : "Unlock Now"}
                  </button>
                ) : (
                  <button
                    onClick={handleUpgrade}
                    disabled={!selectedPkgId || status === "loading"}
                    className="bg-primary-shade3 text-white py-3 px-8 rounded-full hover:shadow-lg disabled:opacity-50 transition"
                  >
                    {status === "loading" ? "Processing..." : "Upgrade Now"}
                  </button>
                ))}
            </div>
          </>
        )}
      </div>
      {/* 🔔 Login Modal */}
      {showLoginPrompt && (
        <LoginPromptModal
          isOpen={showLoginPrompt}
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
    </>
  );
};

export default PackagePlanCard;
